import { Singleton } from 'framework/di';

/**
 * Cart Drawer Recommendation (RCU) Constants.
 *
 * Injectable singleton following the same pattern as `VolumeBundleConstants`
 * and `PredictiveBundleConstants`. This enables Technical Support teams to
 * override selectors, CSS classes, and defaults via the extension system.
 *
 * ## Customization
 * ```javascript
 * window.boostWidgetIntegration.extend('CartDrawerRecommendationConstants', (Base) => {
 *   return class extends Base {
 *     get SELECTORS() {
 *       return { ...super.SELECTORS, CART_MODAL: '#custom-cart-drawer' };
 *     }
 *   };
 * });
 * ```
 */
@Singleton()
export class CartDrawerRecommendationConstants {
  readonly SELECTORS = {
    // Container selectors
    CART_MODAL: '#boost-sd__modal-cart',
    RECOMMENDATION_CONTAINER: '.boost-sd__recommendation--rcu',
    RECOMMENDATION_BLOCK: '.boost-sd__cart-drawer-recommendation-block',

    // Product item selectors
    PRODUCT_ITEM: '.boost-sd__rcu-item',
    PRODUCT_IMAGE: '.boost-sd__rcu-item-image img',
    PRODUCT_INFO: '.boost-sd__rcu-item-info',
    PRODUCT_PRICE: '.boost-sd__rcu-item-price',

    // Variant selectors
    VARIANT_SELECT: '.boost-sd__rcu-item-variant-select',

    // Button selectors
    ADD_TO_CART_BUTTON: '.boost-sd__rcu-item-add-btn',

    // Slider/Carousel selectors
    SLIDER: '.boost-sd__slider',
    SLIDER_CONTAINER: '.boost-sd__slider-container',
    SLIDE: '.boost-sd__slide',
    PREV_BUTTON: '.boost-sd__prev-button',
    NEXT_BUTTON: '.boost-sd__next-button',

    // Dynamic message selectors (created via JS)
    SUCCESS_MESSAGE: '.boost-sd__rcu-item-success',
    ERROR_MESSAGE: '.boost-sd__rcu-item-error',
  } as const;

  readonly CSS_CLASSES = {
    // Product item classes
    PRODUCT_ITEM: 'boost-sd__rcu-item',
    PRODUCT_INFO: 'boost-sd__rcu-item-info',

    // Message classes (for dynamically created elements)
    SUCCESS_MESSAGE: 'boost-sd__rcu-item-success',
    ERROR_MESSAGE: 'boost-sd__rcu-item-error',

    // Button state classes
    BUTTON_LOADING: 'boost-sd__button--loading',
    BUTTON_SUCCESS: 'boost-sd__button--success',
    BUTTON_ERROR: 'boost-sd__button--error',

    // Loading state
    RECOMMENDATION_LOADING: 'boost-sd__recommendation--loading',
  } as const;

  readonly DEFAULTS = {
    /** Default image width in pixels for variant image updates */
    IMAGE_WIDTH: 300,
  } as const;
}
